
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";

interface DailyRecordsProps {
  schoolId?: string;
  searchTerm?: string;
  startDate?: Date;
  termId?: string;
}

interface Visit {
  id: string;
  complaint: string;
  diagnosis: string;
  treatment: string;
  temperature: number;
  date: string;
  students?: {
    surname: string;
    other_names: string;
    class: string;
    stream: string;
  } | null;
  community_members?: {
    name: string;
    category: string;
  } | null;
  staff?: {
    name: string;
    category: string;
  } | null;
}

export const DailyRecords = ({ schoolId, searchTerm = "", startDate = new Date(), termId }: DailyRecordsProps) => {
  const formattedDate = format(startDate, 'yyyy-MM-dd');

  // Create start and end of day for the selected date
  const startOfDay = new Date(startDate);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(startDate);
  endOfDay.setHours(23, 59, 59, 999);

  // Convert to ISO strings for the query
  const startISO = startOfDay.toISOString();
  const endISO = endOfDay.toISOString();

  const { data: visits, isLoading } = useQuery({
    queryKey: ['daily-visits', schoolId, formattedDate, searchTerm],
    queryFn: async () => {
      if (!schoolId) return [];

      const { data, error } = await supabase
        .from('visits')
        .select(`
          id,
          complaint,
          diagnosis,
          treatment,
          temperature,
          date,
          students:student_id (
            surname,
            other_names,
            class,
            stream
          ),
          community_members:community_member_id (
            name,
            category
          )
        `)
        .eq('school_id', schoolId)
        .gte('date', startISO)
        .lte('date', endISO)
        .order('date', { ascending: false });

      if (error) {
        console.error('Error fetching visits:', error);
        throw error;
      }

      return data as Visit[];
    },
    enabled: !!schoolId
  });

  // Filter visits based on search term
  const filteredVisits = visits?.filter((visit) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    
    // Search in student names
    if (visit.students) {
      const fullName = `${visit.students.surname} ${visit.students.other_names}`.toLowerCase();
      if (fullName.includes(searchLower)) return true;
    }
    
    // Search in community member names
    if (visit.community_members) {
      if (visit.community_members.name.toLowerCase().includes(searchLower)) return true;
    }
    
    // Search in staff names (if applicable)
    if (visit.staff) {
      if (visit.staff.name.toLowerCase().includes(searchLower)) return true;
    }
    
    // Search in complaint, diagnosis, treatment
    if (
      visit.complaint.toLowerCase().includes(searchLower) ||
      visit.diagnosis.toLowerCase().includes(searchLower) ||
      visit.treatment.toLowerCase().includes(searchLower)
    ) {
      return true;
    }

    return false;
  }) || [];

  const getVisitorInfo = (visit: Visit) => {
    if (visit.students) {
      return {
        name: `${visit.students.surname} ${visit.students.other_names}`,
        category: `Student - ${visit.students.class} ${visit.students.stream}`,
        type: 'Student'
      };
    }
    if (visit.community_members) {
      return {
        name: visit.community_members.name,
        category: `Community - ${visit.community_members.category.replace(/_/g, ' ')}`,
        type: 'Community'
      };
    }
    if (visit.staff) {
      return {
        name: visit.staff.name,
        category: `Staff - ${visit.staff.category.replace(/_/g, ' ')}`,
        type: 'Staff'
      };
    }
    return {
      name: 'Unknown',
      category: 'Unknown',
      type: 'Unknown'
    };
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>
            {searchTerm ? `Search Results for "${searchTerm}"` : `Today's Visits`} ({filteredVisits.length})
          </CardTitle>
          <CardDescription>
            Dispensary visits on {format(startDate, 'MMMM d, yyyy')}
            {searchTerm && ` matching "${searchTerm}"`}
          </CardDescription>
        </div>
        <Button variant="outline" className="gap-2">
          <FileText size={16} />
          View Report
        </Button>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : filteredVisits.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm 
              ? `No visits found matching "${searchTerm}" on ${format(startDate, 'MMMM d, yyyy')}`
              : `No visits recorded on ${format(startDate, 'MMMM d, yyyy')}`
            }
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Temperature</TableHead>
                  <TableHead>Complaint</TableHead>
                  <TableHead>Diagnosis</TableHead>
                  <TableHead>Treatment</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVisits.map((visit) => {
                  const visitorInfo = getVisitorInfo(visit);
                  return (
                    <TableRow key={visit.id}>
                      <TableCell>{format(new Date(visit.date), 'HH:mm')}</TableCell>
                      <TableCell className="font-medium">{visitorInfo.name}</TableCell>
                      <TableCell>{visitorInfo.category}</TableCell>
                      <TableCell>{visit.temperature}°C</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={visit.complaint}>
                        {visit.complaint}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={visit.diagnosis}>
                        {visit.diagnosis}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={visit.treatment}>
                        {visit.treatment}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          Treat Again
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
